import React, { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ArrowUpRight, 
  ArrowDownLeft, 
  Search, 
  Calendar, 
  ExternalLink,
  Copy,
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  Download
} from 'lucide-react'
import { useTransaction } from '../contexts/TransactionContext'
import { Transaction } from '../contexts/TransactionContext'

interface TransactionListProps {
  limit?: number
  showSearch?: boolean
  showFilters?: boolean
}

const TransactionList: React.FC<TransactionListProps> = ({ 
  limit, 
  showSearch = true, 
  showFilters = true 
}) => {
  const { transactions, clearTransactions, exportTransactions } = useTransaction()
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<'all' | 'send' | 'receive'>('all')
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'completed' | 'failed'>('all')
  const [filterBlockchain, setFilterBlockchain] = useState<'all' | 'ethereum' | 'solana'>('all')
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const filteredTransactions = useMemo(() => {
    let filtered = transactions

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(tx => 
        (tx.hash && tx.hash.toLowerCase().includes(searchTerm.toLowerCase())) ||
        tx.token.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (tx.reason && tx.reason.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    }

    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(tx => tx.type === filterType)
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(tx => tx.status === filterStatus)
    }

    // Blockchain filter
    if (filterBlockchain !== 'all') {
      filtered = filtered.filter(tx => tx.blockchain === filterBlockchain)
    }

    // Sort by timestamp (newest first)
    filtered.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())

    // Apply limit if specified
    if (limit) {
      filtered = filtered.slice(0, limit)
    }

    return filtered
  }, [transactions, searchTerm, filterType, filterStatus, filterBlockchain, limit])

  const getStatusIcon = (status: Transaction['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-400" />
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-400" />
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-400" />
      default:
        return <Clock className="w-4 h-4 text-gray-400" />
    }
  }

  const getStatusColor = (status: Transaction['status']) => {
    switch (status) {
      case 'completed':
        return 'text-green-400'
      case 'failed':
        return 'text-red-400'
      case 'pending':
        return 'text-yellow-400'
      default:
        return 'text-gray-400'
    }
  }

  const formatAddress = (address: string | undefined | null) => {
    if (!address) return 'Unknown'
    return `${address.slice(0, 6)}...${address.slice(-4)}`
  }

  const formatAmount = (amount: string, token: string) => {
    const num = parseFloat(amount)
    if (num < 0.001) {
      return `${amount} ${token}`
    }
    return `${num.toFixed(4)} ${token}`
  }

  const copyToClipboard = async (text: string | undefined, id: string) => {
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      setCopiedId(id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch (error) {
      console.error('Failed to copy:', error)
    }
  }

  const getExplorerUrl = (hash: string | undefined, blockchain: string) => {
    if (!hash) return '#'
    if (blockchain === 'ethereum') {
      return `https://etherscan.io/tx/${hash}`
    } else if (blockchain === 'solana') {
      return `https://explorer.solana.com/tx/${hash}`
    }
    return '#'
  }

  const handleExport = () => {
    exportTransactions()
  }

  const handleClear = () => {
    if (window.confirm('Are you sure you want to clear all transaction history?')) {
      clearTransactions()
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">
          Transaction History
        </h2>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleExport}
            className="glass-button p-2 rounded-lg"
            title="Export transactions"
          >
            <Download className="w-4 h-4" />
          </button>
          <button
            onClick={handleClear}
            className="glass-button p-2 rounded-lg text-red-400 hover:text-red-300"
            title="Clear all transactions"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      {(showSearch || showFilters) && (
        <div className="space-y-3">
          {/* Search */}
          {showSearch && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/50" />
              <input
                type="text"
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 glass rounded-lg border border-white/20 bg-white/5 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>
          )}

          {/* Filters */}
          {showFilters && (
            <div className="flex flex-wrap gap-2">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as any)}
                className="glass rounded-lg border border-white/20 bg-white/5 text-white text-sm px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              >
                <option value="all">All Types</option>
                <option value="send">Sent</option>
                <option value="receive">Received</option>
              </select>

              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as any)}
                className="glass rounded-lg border border-white/20 bg-white/5 text-white text-sm px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>

              <select
                value={filterBlockchain}
                onChange={(e) => setFilterBlockchain(e.target.value as any)}
                className="glass rounded-lg border border-white/20 bg-white/5 text-white text-sm px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              >
                <option value="all">All Chains</option>
                <option value="ethereum">Ethereum</option>
                <option value="solana">Solana</option>
              </select>
            </div>
          )}
        </div>
      )}

      {/* Transaction List */}
      <div className="space-y-3">
        <AnimatePresence>
          {filteredTransactions.length === 0 ? (
            <motion.div
              className="text-center py-8 text-white/50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No transactions found</p>
              {searchTerm && (
                <p className="text-sm mt-1">Try adjusting your search or filters</p>
              )}
            </motion.div>
          ) : (
            filteredTransactions.map((transaction, index) => (
              <motion.div
                key={transaction.id}
                className="glass rounded-lg border border-white/20 p-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    {/* Transaction Type Icon */}
                    <div className={`p-2 rounded-lg ${
                      transaction.type === 'send' 
                        ? 'bg-red-500/20 text-red-400' 
                        : 'bg-green-500/20 text-green-400'
                    }`}>
                      {transaction.type === 'send' ? (
                        <ArrowUpRight className="w-4 h-4" />
                      ) : (
                        <ArrowDownLeft className="w-4 h-4" />
                      )}
                    </div>

                    {/* Transaction Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-medium text-white">
                          {transaction.type === 'send' ? 'Sent' : 'Received'}
                        </span>
                        <span className="text-white font-bold">
                          {formatAmount(transaction.amount, transaction.token)}
                        </span>
                        <span className="text-xs px-2 py-1 rounded-full bg-white/10 text-white/70">
                          {transaction.blockchain.toUpperCase()}
                        </span>
                      </div>

                      <div className="space-y-1 text-sm text-white/70">

                        {transaction.reason && (
                          <div className="flex items-center space-x-2">
                            <span>Reason:</span>
                            <span className="text-white/90">{transaction.reason}</span>
                          </div>
                        )}

                        <div className="flex items-center space-x-2">
                          <span>Hash:</span>
                          <button
                            onClick={() => copyToClipboard(transaction.hash, `hash-${transaction.id}`)}
                            className="hover:text-white transition-colors"
                          >
                            {formatAddress(transaction.hash)}
                          </button>
                          {copiedId === `hash-${transaction.id}` ? (
                            <CheckCircle className="w-3 h-3 text-green-400" />
                          ) : (
                            <Copy className="w-3 h-3 hover:text-white cursor-pointer" />
                          )}
                          <a
                            href={getExplorerUrl(transaction.hash, transaction.blockchain)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-white transition-colors"
                          >
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Status and Timestamp */}
                  <div className="text-right">
                    <div className="flex items-center space-x-1 mb-1">
                      {getStatusIcon(transaction.status)}
                      <span className={`text-sm font-medium ${getStatusColor(transaction.status)}`}>
                        {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                      </span>
                    </div>
                    <div className="text-xs text-white/50">
                      {new Date(transaction.timestamp).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-white/50">
                      {new Date(transaction.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </div>

      {/* Load More */}
      {limit && filteredTransactions.length === limit && transactions.length > limit && (
        <div className="text-center">
          <button className="glass-button px-4 py-2 rounded-lg text-white/70 hover:text-white">
            Load More
          </button>
        </div>
      )}
    </div>
  )
}

export default TransactionList